// Configuración → Portada del sitio (§1.3): los bloques de la portada vieja repartidos en
// seis pestañas, sin perder ningún campo. Barra «Hay cambios sin guardar» solo con cambios
// y aviso al irse sin guardar. Manda solo los campos de la portada (los de Accesos no):
// el servidor guarda con merge (Ficha 8, P-10).
import React, { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { adminFetch } from "../api";
import { Alert, Button, Card, Field, ICON, Input, Select, Seg, Skeleton, Toggle, cx, useToast } from "./ui";
import { ConfigPage, type CfgView } from "./ConfigPage";
import { ImageUploader } from "./ImageUploader";
import { DEFAULT_FRONTPAGE, PORTADA_KEYS, type FrontpageForm, type PortadaForm } from "./frontpageDefaults";

type Tab = "banners" | "placa" | "listado" | "whatsapp" | "publicidad" | "pie";

const TABS: Array<{ value: Tab; label: string }> = [
  { value: "banners", label: "Banners" },
  { value: "placa", label: "Placa" },
  { value: "listado", label: "Listado" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "publicidad", label: "Publicidad" },
  { value: "pie", label: "Pie" },
];

const BANNER_COLORS = ["#2f6f4f", "#5cc08f", "#17181a", "#ffffff"];
const PLACA_COLORS = ["#10b981", "#2563eb", "#1e293b", "#0f172a", "#7c3aed", "#e11d48", "#0d9488"];
const FOOTER_COLORS = ["#1e293b", "#0f172a", "#111827", "#1c1917", "#1a2e05", "#162e3d", "#312e81"];

const SIZE_OPTIONS = [
  { value: "xs", label: "Muy chico" },
  { value: "sm", label: "Chico" },
  { value: "base", label: "Normal" },
  { value: "lg", label: "Grande" },
  { value: "xl", label: "Muy grande" },
];
const WEIGHT_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "medium", label: "Medio" },
  { value: "semibold", label: "Seminegrita" },
  { value: "bold", label: "Negrita" },
  { value: "extrabold", label: "Extranegrita" },
];
const TEXT_COLOR_OPTIONS = [
  { value: "white", label: "Blanco" },
  { value: "slate-900", label: "Gris oscuro" },
  { value: "slate-800", label: "Gris medio" },
  { value: "emerald-900", label: "Verde oscuro" },
  { value: "yellow-300", label: "Amarillo oro" },
];
const PROMO_POSITIONS = [
  { value: "above_location", label: "Encima de la ubicación" },
  { value: "below_location", label: "Debajo de la ubicación" },
  { value: "above_pharmacies", label: "Arriba de las farmacias" },
  { value: "below_pharmacies", label: "Debajo de las farmacias" },
];

// No hay Textarea en ui.tsx: mismo aspecto que Input (CONTROL de ui.tsx), con alto libre.
const TEXTAREA =
  "w-full rounded-[10px] border border-adm-border-2 bg-adm-surface px-3 py-2.5 text-sm text-adm-text outline-none transition-[border-color,box-shadow] placeholder:text-adm-t40 focus:border-adm-acento focus:shadow-[0_0_0_3px_var(--acento-suave)]";

function pickPortada(config: Partial<FrontpageForm>): PortadaForm {
  const out: any = {};
  for (const key of PORTADA_KEYS) out[key] = config[key] ?? DEFAULT_FRONTPAGE[key];
  return out as PortadaForm;
}

const sameForm = (a: PortadaForm, b: PortadaForm) => PORTADA_KEYS.every((key) => a[key] === b[key]);

function Section({ title, sub, toggle, children }: { title: string; sub?: string; toggle?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <Card className="flex flex-col gap-[18px] px-4 py-[18px] md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-[15px] font-semibold text-adm-text">{title}</h3>
          {sub && <p className="text-[13px] leading-[1.55] text-adm-t55">{sub}</p>}
        </div>
        {toggle}
      </div>
      {children}
    </Card>
  );
}

function Grid({ cols = 2, children }: { cols?: 2 | 3; children?: React.ReactNode }) {
  return <div className={cx("grid grid-cols-1 gap-4", cols === 3 ? "md:grid-cols-3" : "md:grid-cols-2")}>{children}</div>;
}

function ColorField({ id, label, value, presets, onChange }: { id: string; label: string; value: string; presets: string[]; onChange: (v: string) => void }) {
  const current = (value || "").toLowerCase();
  return (
    <Field label={label} htmlFor={id}>
      <div className="flex flex-wrap items-center gap-2">
        {presets.map((c) => (
          <button
            key={c}
            type="button"
            title={c}
            aria-label={`Usar ${c}`}
            aria-pressed={current === c}
            onClick={() => onChange(c)}
            style={{ backgroundColor: c }}
            className={cx(
              "size-[30px] cursor-pointer rounded-lg border border-adm-border max-md:size-11",
              current === c && "outline outline-2 outline-offset-2 outline-adm-acento",
            )}
          />
        ))}
        <input
          type="color"
          aria-label={`${label}: elegir otro color`}
          value={/^#[0-9a-f]{6}$/i.test(value) ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="h-[30px] w-[38px] cursor-pointer rounded-lg border border-adm-border bg-adm-surface p-0.5 max-md:h-11 max-md:w-11"
        />
        <div style={{ width: 110 }}>
          <Input id={id} mono value={value} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)} />
        </div>
      </div>
    </Field>
  );
}

function OptionSelect({ id, label, value, options, onChange }: { id: string; label: string; value: string; options: Array<{ value: string; label: string }>; onChange: (v: string) => void }) {
  return (
    <Field label={label} htmlFor={id}>
      <Select id={id} value={value} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}

export function PortadaPage({
  config,
  loading,
  loadFailed,
  onReload,
  onNavigate,
  onSaved,
  onDirtyChange,
}: {
  config: FrontpageForm;
  /** primera carga en curso */
  loading: boolean;
  /** no llegó la configuración guardada: no se muestra el formulario */
  loadFailed: boolean;
  onReload: () => Promise<unknown>; // fetchData devuelve boolean desde la Ficha 4
  onNavigate: (view: CfgView) => void;
  /** recibe la configuración completa que devolvió el servidor */
  onSaved: (config: FrontpageForm) => void;
  /** App.tsx lo usa para preguntar antes de salir de la pantalla */
  onDirtyChange: (dirty: boolean) => void;
}) {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>("banners");
  const [baseline, setBaseline] = useState<PortadaForm>(() => pickPortada(config));
  const [draft, setDraft] = useState<PortadaForm>(baseline);
  const [saving, setSaving] = useState(false);
  const dirty = !sameForm(draft, baseline);

  // Lo guardado manda mientras no haya nada a medio editar.
  useEffect(() => {
    if (!dirty) {
      const next = pickPortada(config);
      setBaseline(next);
      setDraft(next);
    }
  }, [config]);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty]);
  useEffect(() => () => onDirtyChange(false), []);

  // Cerrar la pestaña o recargar con cambios sin guardar: el navegador pregunta.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const set = <K extends keyof PortadaForm>(key: K, value: PortadaForm[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const text = (key: keyof PortadaForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => set(key, e.target.value as any);

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminFetch("/api/frontpage/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) throw new Error(data?.error || `El servidor respondió ${res.status}.`);
      const next = pickPortada(data.config);
      setBaseline(next);
      setDraft(next);
      onSaved(data.config);
      toast.show("Portada publicada");
    } catch (err: any) {
      toast.show(`No se pudo guardar la portada. ${err?.message || ""}`.trim(), "error");
    } finally {
      setSaving(false);
    }
  };

  const actions = (
    <Button href="/" target="_blank" icon={<ExternalLink {...ICON} />}>
      Ver la portada
    </Button>
  );

  if (loading && !dirty) {
    return (
      <ConfigPage view="cfg:portada" role="super" onNavigate={onNavigate} actions={actions}>
        <Skeleton className="h-11 w-full max-w-[520px] rounded-xl" />
        <Skeleton className="h-[320px] rounded-[18px]" />
      </ConfigPage>
    );
  }

  if (loadFailed) {
    return (
      <ConfigPage view="cfg:portada" role="super" onNavigate={onNavigate} actions={actions}>
        <Alert tone="red" action={<Button size="sm" onClick={() => onReload()}>Reintentar</Button>}>
          No se pudo leer la portada guardada. Para no publicar valores de fábrica encima, el formulario aparece cuando cargue.
        </Alert>
      </ConfigPage>
    );
  }

  return (
    <ConfigPage view="cfg:portada" role="super" onNavigate={onNavigate} actions={actions}>
      <Seg<Tab> label="Partes de la portada" value={tab} onChange={setTab} options={TABS} />

      {tab === "banners" && (
        <>
          <Section title="Banner superior" sub="Arriba de todo, antes de las farmacias de turno.">
            <Grid>
              <ImageUploader label="Escritorio" icon="monitor" hint="Ideal: de 1200 a 1920 px de ancho" allowUrl value={draft.topBannerImage} onChange={(v) => set("topBannerImage", v)} />
              <ImageUploader label="Celular" icon="smartphone" tall emptyText="Usa la de escritorio" hint="Ideal: de 360 a 640 px de ancho" allowUrl value={draft.topBannerMobileImage} onChange={(v) => set("topBannerMobileImage", v)} />
            </Grid>
            <Grid>
              <Field label="Link al tocarlo" htmlFor="pt-top-link">
                <Input id="pt-top-link" inputMode="url" placeholder="https://colfarmatandil.org.ar" value={draft.topBannerLink} onChange={text("topBannerLink")} />
              </Field>
              <Field label="Alto" htmlFor="pt-top-height" hint="En píxeles.">
                <Input id="pt-top-height" type="number" min={1} value={draft.topBannerHeight} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("topBannerHeight", parseInt(e.target.value) || 90)} />
              </Field>
            </Grid>
            <Field label="Texto, si no hay imagen" htmlFor="pt-top-text">
              <Input id="pt-top-text" value={draft.topBannerText} onChange={text("topBannerText")} />
            </Field>
            <ColorField id="pt-top-bg" label="Color de fondo" presets={BANNER_COLORS} value={draft.topBannerBgColor} onChange={(v) => set("topBannerBgColor", v)} />
            <div className="flex flex-wrap gap-x-6">
              <Toggle checked={draft.hideTopBannerMobile} onChange={(v) => set("hideTopBannerMobile", v)} label="Ocultar en celular" />
              <Toggle checked={draft.hideTopBannerDesktop} onChange={(v) => set("hideTopBannerDesktop", v)} label="Ocultar en escritorio" />
            </div>
          </Section>

          <Section title="Segundo banner" sub="Debajo del listado de farmacias.">
            <Grid>
              <ImageUploader label="Escritorio" icon="monitor" hint="Ideal: de 1200 a 1920 px de ancho" allowUrl value={draft.bottomBannerImage} onChange={(v) => set("bottomBannerImage", v)} />
              <ImageUploader label="Celular" icon="smartphone" tall emptyText="Usa la de escritorio" hint="Ideal: de 360 a 640 px de ancho" allowUrl value={draft.bottomBannerMobileImage} onChange={(v) => set("bottomBannerMobileImage", v)} />
            </Grid>
            <Grid>
              <Field label="Link al tocarlo" htmlFor="pt-bottom-link">
                <Input id="pt-bottom-link" inputMode="url" placeholder="https://…" value={draft.bottomBannerLink} onChange={text("bottomBannerLink")} />
              </Field>
              <Field label="Alto máximo" htmlFor="pt-bottom-height" hint="En píxeles.">
                <Input id="pt-bottom-height" type="number" min={1} value={draft.bottomBannerHeight} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("bottomBannerHeight", parseInt(e.target.value) || 170)} />
              </Field>
            </Grid>
            <Field label="Texto, si no hay imagen" htmlFor="pt-bottom-text">
              <textarea id="pt-bottom-text" rows={2} className={TEXTAREA} value={draft.bottomBannerText} onChange={text("bottomBannerText")} />
            </Field>
            <ColorField id="pt-bottom-bg" label="Color de fondo" presets={BANNER_COLORS} value={draft.bottomBannerBgColor} onChange={(v) => set("bottomBannerBgColor", v)} />
          </Section>
        </>
      )}

      {tab === "placa" && (
        <>
          <Section title="Placa de cada farmacia" sub="El recuadro con los datos de la farmacia de turno.">
            <ColorField id="pt-placa" label="Color de fondo" presets={PLACA_COLORS} value={draft.detailsBoxColor} onChange={(v) => set("detailsBoxColor", v)} />
            <div className="flex flex-col gap-0.5">
              <Toggle checked={draft.showPhotoOnMobile !== false} onChange={(v) => set("showPhotoOnMobile", v)} label="Mostrar la foto de la farmacia en el celular" />
              <span className="text-xs text-adm-t55">Si lo apagás, en el celular se ve una lista compacta con el nombre arriba.</span>
            </div>
          </Section>
          <Section title="Letra de la dirección">
            <Grid cols={3}>
              <OptionSelect id="pt-addr-size" label="Tamaño" options={SIZE_OPTIONS} value={draft.addressFontSize || "sm"} onChange={(v) => set("addressFontSize", v)} />
              <OptionSelect id="pt-addr-weight" label="Grosor" options={WEIGHT_OPTIONS} value={draft.addressFontWeight || "semibold"} onChange={(v) => set("addressFontWeight", v)} />
              <OptionSelect id="pt-addr-color" label="Color" options={TEXT_COLOR_OPTIONS} value={draft.addressFontColor || "white"} onChange={(v) => set("addressFontColor", v)} />
            </Grid>
          </Section>
          <Section title="Letra del teléfono">
            <Grid cols={3}>
              <OptionSelect id="pt-phone-size" label="Tamaño" options={SIZE_OPTIONS} value={draft.phoneFontSize || "sm"} onChange={(v) => set("phoneFontSize", v)} />
              <OptionSelect id="pt-phone-weight" label="Grosor" options={WEIGHT_OPTIONS} value={draft.phoneFontWeight || "semibold"} onChange={(v) => set("phoneFontWeight", v)} />
              <OptionSelect id="pt-phone-color" label="Color" options={TEXT_COLOR_OPTIONS} value={draft.phoneFontColor || "white"} onChange={(v) => set("phoneFontColor", v)} />
            </Grid>
          </Section>
        </>
      )}

      {tab === "listado" && (
        <>
          <Section title="Listado de farmacias" sub="Cómo se acomodan las farmacias de turno en la portada.">
            <Grid>
              <OptionSelect
                id="pt-per-row"
                label="Farmacias por fila en escritorio"
                value={String(draft.pharmaciesPerRow || 2)}
                options={[
                  { value: "1", label: "1, a todo el ancho" },
                  { value: "2", label: "2 (recomendado)" },
                  { value: "3", label: "3" },
                  { value: "4", label: "4, compacto" },
                ]}
                onChange={(v) => set("pharmaciesPerRow", parseInt(v) || 2)}
              />
              <OptionSelect
                id="pt-location"
                label="Recuadro «¿Cuál me queda más cerca?»"
                value={draft.locationBoxPosition || "above"}
                options={[
                  { value: "above", label: "Arriba de las farmacias" },
                  { value: "below", label: "Debajo de las farmacias" },
                ]}
                onChange={(v) => set("locationBoxPosition", v)}
              />
            </Grid>
            <Toggle checked={draft.carouselDesktop} onChange={(v) => set("carouselDesktop", v)} label="Mostrar como carrusel, con flechas y puntos" />
          </Section>
          <Section title="Cuenta regresiva" sub="El recuadro que muestra cuánto falta para el cambio de turno.">
            <div className="flex flex-col">
              <Toggle checked={draft.hideCountdownFrame} onChange={(v) => set("hideCountdownFrame", v)} label="Sin recuadro ni fondo" />
              <Toggle checked={draft.countdownBelowToggle} onChange={(v) => set("countdownBelowToggle", v)} label="Debajo del botón «Ver farmacias del siguiente turno»" />
            </div>
          </Section>
        </>
      )}

      {tab === "whatsapp" && (
        <>
          <Section
            title="Banner de WhatsApp"
            sub="Invita a consultar los turnos por WhatsApp."
            toggle={<Toggle checked={draft.showWhatsAppBanner} onChange={(v) => set("showWhatsAppBanner", v)} label="Visible" />}
          >
            <Field label="Texto" htmlFor="pt-wa-text">
              <Input id="pt-wa-text" placeholder="📲 Consultá quién está de turno hoy o mañana por WhatsApp." value={draft.whatsAppBannerText} onChange={text("whatsAppBannerText")} />
            </Field>
            <Grid>
              <Field label="Número que recibe las consultas" htmlFor="pt-wa-phone" hint="Con código de país y sin espacios.">
                <Input id="pt-wa-phone" mono inputMode="tel" placeholder="5492494123456" value={draft.whatsAppConsultPhone} onChange={text("whatsAppConsultPhone")} />
              </Field>
              <Field label="Mensaje con el que se abre el chat" htmlFor="pt-wa-default">
                <Input id="pt-wa-default" placeholder="Hoy" value={draft.whatsAppConsultDefaultText} onChange={text("whatsAppConsultDefaultText")} />
              </Field>
            </Grid>
            <OptionSelect
              id="pt-wa-position"
              label="Ubicación"
              value={draft.whatsAppBannerPosition || "top"}
              options={[
                { value: "top", label: "Arriba de las farmacias" },
                { value: "bottom", label: "Debajo de las farmacias" },
              ]}
              onChange={(v) => set("whatsAppBannerPosition", v)}
            />
          </Section>
          <Section
            title="Botón flotante"
            sub="Un botón de WhatsApp fijo en la esquina de abajo."
            toggle={<Toggle checked={draft.showWhatsAppFloatingButton} onChange={(v) => set("showWhatsAppFloatingButton", v)} label="Visible" />}
          />
        </>
      )}

      {tab === "publicidad" && (
        <Section
          title="Módulo publicitario"
          sub="Imagen o GIF: 320 × 720 en el celular y 1230 × 140 en escritorio."
          toggle={<Toggle checked={draft.promoModuleEnabled} onChange={(v) => set("promoModuleEnabled", v)} label="Visible" />}
        >
          <OptionSelect id="pt-promo-position" label="Ubicación" options={PROMO_POSITIONS} value={draft.promoModulePosition || "above_location"} onChange={(v) => set("promoModulePosition", v)} />
          <Grid>
            <ImageUploader label="Celular (320 × 720)" icon="smartphone" tall allowUrl showCompressionToggle defaultCompress value={draft.promoModuleImage} onChange={(v) => set("promoModuleImage", v)} />
            <ImageUploader label="Escritorio (1230 × 140)" icon="monitor" allowUrl showCompressionToggle defaultCompress value={draft.promoModuleDesktopImage} onChange={(v) => set("promoModuleDesktopImage", v)} />
          </Grid>
          <Grid>
            <Field label="Link al tocarlo" htmlFor="pt-promo-link" hint="Opcional.">
              <Input id="pt-promo-link" inputMode="url" placeholder="https://wa.me/… o https://tusitio.com" value={draft.promoModuleLink} onChange={text("promoModuleLink")} />
            </Field>
            <Field label="Descripción de la imagen" htmlFor="pt-promo-alt" hint="La leen los lectores de pantalla.">
              <Input id="pt-promo-alt" placeholder="Módulo publicitario" value={draft.promoModuleAlt} onChange={text("promoModuleAlt")} />
            </Field>
          </Grid>
        </Section>
      )}

      {tab === "pie" && (
        <>
          <Section title="Contacto" sub="La columna izquierda del pie.">
            <ImageUploader label="Logo" icon="image" value={draft.footerLogoUrl} onChange={(v) => set("footerLogoUrl", v)} />
            <Grid cols={3}>
              <Field label="Dirección" htmlFor="pt-foot-address">
                <Input id="pt-foot-address" value={draft.footerAddress} onChange={text("footerAddress")} />
              </Field>
              <Field label="Teléfono" htmlFor="pt-foot-phone">
                <Input id="pt-foot-phone" inputMode="tel" value={draft.footerPhone} onChange={text("footerPhone")} />
              </Field>
              <Field label="Correo" htmlFor="pt-foot-email">
                <Input id="pt-foot-email" type="email" value={draft.footerEmail} onChange={text("footerEmail")} />
              </Field>
            </Grid>
          </Section>
          <Section title="Redes">
            <Field label="Texto sobre las redes" htmlFor="pt-foot-socials">
              <Input id="pt-foot-socials" placeholder="Seguinos en redes sociales" value={draft.footerSocialsText} onChange={text("footerSocialsText")} />
            </Field>
            <Grid>
              <Field label="Facebook" htmlFor="pt-foot-fb">
                <Input id="pt-foot-fb" inputMode="url" placeholder="https://facebook.com/…" value={draft.footerFacebookUrl} onChange={text("footerFacebookUrl")} />
              </Field>
              <Field label="Instagram" htmlFor="pt-foot-ig">
                <Input id="pt-foot-ig" inputMode="url" placeholder="https://instagram.com/…" value={draft.footerInstagramUrl} onChange={text("footerInstagramUrl")} />
              </Field>
            </Grid>
          </Section>
          <Section title="Fondo y derechos">
            <ColorField id="pt-foot-bg" label="Color de fondo" presets={FOOTER_COLORS} value={draft.footerBgColor} onChange={(v) => set("footerBgColor", v)} />
            <Field label="Texto de derechos" htmlFor="pt-foot-copy">
              <textarea id="pt-foot-copy" rows={2} className={TEXTAREA} value={draft.footerCopyrightText} onChange={text("footerCopyrightText")} />
            </Field>
            <Field label="Link de los derechos" htmlFor="pt-foot-copy-link" hint="Opcional.">
              <Input id="pt-foot-copy-link" inputMode="url" placeholder="https://…" value={draft.footerCopyrightLink} onChange={text("footerCopyrightLink")} />
            </Field>
          </Section>
        </>
      )}

      {dirty && (
        <div className="sticky bottom-4 z-40 flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-adm-border bg-adm-surface py-2.5 pl-[18px] pr-2.5 text-sm text-adm-text shadow-adm">
          <span>Hay cambios sin guardar</span>
          <div className="flex gap-2 max-md:w-full max-md:[&>*]:flex-1">
            <Button variant="ghost" onClick={() => setDraft(baseline)} disabled={saving}>
              Descartar
            </Button>
            <Button variant="primary" onClick={save} loading={saving}>
              Guardar y publicar
            </Button>
          </div>
        </div>
      )}
    </ConfigPage>
  );
}
